// Sparkline — micro-gráfica SVG inline, sin dependencias externas (no Recharts).
//
// Renderiza una polyline + relleno suave. Acepta puntos con valor `null` (gaps).
// Para los gaps, se rompe la línea (path con M tras los null).

import { cn } from '../../lib/utils';

export interface SparklinePunto {
  fecha: string;
  valor: number | null;
}

export interface SparklineProps {
  puntos: SparklinePunto[];
  /** Tono del trazo. */
  tone?: 'ok' | 'warn' | 'crit' | 'neutral';
  width?: number;
  height?: number;
  className?: string;
}

const toneStroke: Record<NonNullable<SparklineProps['tone']>, string> = {
  ok: 'stroke-ok',
  warn: 'stroke-warn',
  crit: 'stroke-crit',
  neutral: 'stroke-fg-tertiary',
};

const toneFill: Record<NonNullable<SparklineProps['tone']>, string> = {
  ok: 'fill-ok',
  warn: 'fill-warn',
  crit: 'fill-crit',
  neutral: 'fill-fg-tertiary',
};

export function Sparkline({
  puntos,
  tone = 'neutral',
  width = 96,
  height = 24,
  className,
}: SparklineProps) {
  if (puntos.length === 0) return null;

  const valores = puntos.map((p) => p.valor);
  const validos = valores.filter((v): v is number => v !== null);
  if (validos.length === 0) return null;

  const min = Math.min(...validos);
  const max = Math.max(...validos);
  const rango = max - min || 1;
  const padding = 2;

  const stepX = puntos.length > 1 ? (width - padding * 2) / (puntos.length - 1) : 0;

  // Construir path con saltos (M) cuando hay nulls.
  const pathParts: string[] = [];
  let lastWasNull = true;
  puntos.forEach((p, i) => {
    if (p.valor === null) {
      lastWasNull = true;
      return;
    }
    const x = padding + i * stepX;
    const y = padding + (1 - (p.valor - min) / rango) * (height - padding * 2);
    pathParts.push(`${lastWasNull ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`);
    lastWasNull = false;
  });

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={cn('overflow-visible', className)}
      aria-hidden="true"
    >
      <path
        d={pathParts.join(' ')}
        className={cn(toneStroke[tone], 'fill-none')}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Punto final destacado */}
      {(() => {
        for (let i = puntos.length - 1; i >= 0; i--) {
          const v = puntos[i]?.valor;
          if (v === null || v === undefined) continue;
          const x = padding + i * stepX;
          const y = padding + (1 - (v - min) / rango) * (height - padding * 2);
          return <circle cx={x} cy={y} r={2} className={toneFill[tone]} />;
        }
        return null;
      })()}
    </svg>
  );
}
